import Grid from '@mui/material/Grid';
import BuildIcon from '@mui/icons-material/Build';
import { EntitySwitch } from '@backstage/plugin-catalog';
import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import { EntityGithubDeploymentsCard } from '@backstage-community/plugin-github-deployments';
import {
  EntityJenkinsContent,
  isJenkinsAvailable,
} from '@backstage-community/plugin-jenkins';
import { GitHubGuard } from '@mercedes-benz/permission';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '@mercedes-benz/odp-theme-v2';

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    emptyRoot: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      padding: 48,
      minHeight: 400,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      textAlign: 'center',
    },
    iconWrapper: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      background: 'rgba(0, 120, 214, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        width: 28,
        height: 28,
        color: c.primary.main,
      },
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: c.text.primary,
      fontWeight: 600,
      fontSize: '1.1rem',
      margin: 0,
      userSelect: 'none',
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: fgAlpha(0.55),
      fontSize: '0.875rem',
      margin: 0,
      maxWidth: 480,
      lineHeight: 1.6,
    },
    link: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: c.text.link,
      textDecoration: 'none',
      padding: '8px 20px',
      borderRadius: 20,
      border: `1px solid ${c.primary.main}`,
      transition: 'background 0.15s ease',
      '&:hover': {
        background: 'rgba(0, 120, 214, 0.08)',
        textDecoration: 'none',
      },
    },
  };
});

const NoCICDConfigured = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.emptyRoot}>
      <div className={classes.iconWrapper}>
        <BuildIcon />
      </div>
      <h3 className={classes.title}>No CI/CD available for this entity</h3>
      <p className={classes.description}>
        Add an annotation to your component to enable CI/CD integration. Jenkins
        and GitHub Actions are supported.
      </p>
      <a
        href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
        target="_blank"
        rel="noopener noreferrer"
        className={classes.link}
      >
        Read more about annotations
      </a>
    </div>
  );
};

export const CICDTab = () => (
  <EntitySwitch>
    <EntitySwitch.Case if={isJenkinsAvailable}>
      <EntityJenkinsContent />
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <GitHubGuard feature="Actions and Deployments">
        <Grid container spacing={3} alignItems="stretch" direction="row">
          <Grid item xs={12}>
            <EntityGithubDeploymentsCard />
          </Grid>
          <Grid item xs={12}>
            <EntityGithubActionsContent />
          </Grid>
        </Grid>
      </GitHubGuard>
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <NoCICDConfigured />
    </EntitySwitch.Case>
  </EntitySwitch>
);
